const principles = [
  {
    title: 'BEGIN AND END WITH THE FICTION',
    text: 'Use the fiction to drive mechanics, then connect the mechanics back to the fiction.',
  },
  {
    title: 'COLLABORATE AT ALL TIMES, ESPECIALLY DURING CONFLICT',
    text: 'The PCs are the protagonists of the campaign; antagonism between player and GM should exist only in the fiction.',
  },
  {
    title: 'FILL THE WORLD WITH LIFE, WONDER, AND DANGER',
    text: 'Showcase rich cultures, take the PCs to wondrous places, and introduce them to dangerous creatures.',
  },
  {
    title: 'ASK QUESTIONS AND INCORPORATE THE ANSWERS',
    text: "Ensuring that the players' ideas are included results in a narrative that supports the whole group's creativity.",
  },
  {
    title: 'GIVE EVERY ROLL IMPACT',
    text: 'Only ask the players to roll during meaningful moments.',
  },
  {
    title: 'PLAY TO FIND OUT WHAT HAPPENS',
    text: 'Be surprised by what the characters do, the choices they make, and the people they become.',
  },
  {
    title: 'HOLD ON GENTLY',
    text: "Don't worry if you need to abandon or alter something that came before.",
  },
];

const practices = [
  { title: 'CULTIVATE A CURIOUS TABLE', text: "Follow what catches the players' interest to foster an environment of creative inquiry." },
  { title: "GAIN YOUR PLAYERS' TRUST", text: 'Act in good faith, follow through on your promises, admit your mistakes.' },
  { title: 'KEEP THE STORY MOVING FORWARD', text: 'Advance the story through escalating action, new information, or changing circumstances after every action roll, whether it succeeds or fails.' },
  { title: 'CUT TO THE ACTION', text: 'Skip past the boring bits. When a scene drags on, end it.' },
  { title: 'HELP THE PLAYERS USE THE GAME', text: 'Players have more fun when you help them understand the system.' },
  { title: 'CREATE A META CONVERSATION', text: 'Empower players to speak out of character, use safety tools, and ask for clarification.' },
  { title: 'TELL THEM WHAT THEY WOULD KNOW', text: "Don't hide obvious details or important information from the players." },
  { title: 'GROUND THE WORLD IN MOTIVE', text: "An NPC's actions flow from their goals and desires." },
  { title: "BRING THE GAME'S MECHANICS TO LIFE", text: 'Set a good example of how fiction and mechanics work together to enhance the game experience.' },
  { title: 'REFRAME RATHER THAN REJECT', text: "If a player's contribution conflicts with the fiction, work with them to reshape it." },
  { title: 'WORK IN MOMENTS AND MONTAGES', text: "When framing a scene, decide which beats should be savored and which shouldn't linger." },
];

const pitfalls = [
  {
    title: 'UNDERMINING THE HEROES',
    text: "If a roll doesn't go well, show how it was impacted by an adversary's prowess, environmental factors, or unexpected surprises, rather than the PC's incompetence.",
  },
  {
    title: 'ALWAYS TELLING THE PLAYERS WHAT TO ROLL',
    text: 'Let the players decide how to handle a challenge.',
  },
  {
    title: 'LETTING SCENES DRAG',
    text: "Shake it up or cut away when a scene has concluded, the table's energy is flagging, or people are talking in circles.",
  },
  {
    title: 'SINGULAR SOLUTIONS',
    text: "Don't get hung up on one right answer to a problem. If the players have a clever idea, make it work.",
  },
  {
    title: 'OVERPLANNING',
    text: 'Spend your prep time inventing situations instead of scripting scenes. If the players surprise you, take a break to think through your options.',
  },
  {
    title: 'HOARDING FEAR',
    text: 'Spend Fear when you have the opportunity. The players will always generate more.',
  },
];

export default function GuidanceContent() {
  return (
    <>
      <h3 id="introduction">INTRODUCTION</h3>
      <p>The GM is responsible for guiding the narrative and roleplaying the world the PCs inhabit. This section provides you with advice for running Daggerheart: using the core mechanics; creating memorable encounters; planning exciting sessions; selecting, creating, and using GM moves; crafting a full campaign; running dynamic NPCs; and more.</p>

      <h3 id="gm-guidance">GM GUIDANCE</h3>
      <p>These three sections provide a foundation to help you get the most out of this game. The "GM Principles" are your guiding star — when in doubt, return to these principles.</p>

      <h3 id="gm-principles">GM PRINCIPLES</h3>
      <div className="feature-grid">
        {principles.map((p) => (
          <div key={p.title} className="feature-block">
            <div className="feature-name">{p.title}</div>
            <div className="feature-text">{p.text}</div>
          </div>
        ))}
      </div>

      <h3 id="gm-practices">GM PRACTICES</h3>
      <div className="feature-grid">
        {practices.map((p) => (
          <div key={p.title} className="feature-block">
            <div className="feature-name">{p.title}</div>
            <div className="feature-text">{p.text}</div>
          </div>
        ))}
      </div>

      <h3 id="pitfalls">PITFALLS TO AVOID</h3>
      <div className="feature-grid">
        {pitfalls.map((p) => (
          <div key={p.title} className="feature-block">
            <div className="feature-name">{p.title}</div>
            <div className="feature-text">{p.text}</div>
          </div>
        ))}
      </div>
      <p className="rule-note">For more in-depth GM guidance, see pg. 140 of the Daggerheart Core Rulebook.</p>
    </>
  );
}
